import { IItemProperty } from "../interfaces/IItem";
import { IMFilesProperty, ILookup } from "../interfaces/mfiles/IMFilesObject";
import { PropertyDataType } from "../constants/datatypes";
import { IDefaultPropertyValue } from "../interfaces/configuration/IConfiguration";
import IMFilesPropertyDefinition from "../interfaces/mfiles/IMFilesPropertyDefinition";
import { MFILES_DATE_VALUE_FORMAT } from "../constants/common";
import { format } from "date-fns";

export const createMfilesProperty = (propertyId: number, dataType: PropertyDataType, value: string | null): IMFilesProperty => {
  const isLookup = dataType === PropertyDataType.Lookup;
  const isMultiSelectLookup = dataType === PropertyDataType.MultiSelectLookup;

  if (value == null) {
    return {
      PropertyDef: propertyId,
      TypedValue: {
        DataType: dataType,
        HasValue: false,
        DisplayValue: "",
        Value: null,
        Lookup: null,
        Lookups: null,
      },
    };
  } else {
    return {
      PropertyDef: propertyId,
      TypedValue: {
        DataType: dataType,
        HasValue: true,
        DisplayValue: "",
        Value: !isLookup && !isMultiSelectLookup ? value : null,
        Lookup: isLookup ? toLookup(value) : null,
        Lookups: isMultiSelectLookup ? value.split(",").map((id) => toLookup(id)) : null,
      },
    };
  }
};

export const mapGanttPropertyToMfilesProperty = (ganttProperty: IItemProperty): IMFilesProperty => {
  return createMfilesProperty(ganttProperty.property, ganttProperty.dataType, ganttProperty.value);
};

export const mapMfilesPropertyToGanttProperty = (mfilesProperty: IMFilesProperty): IItemProperty => {
  return {
    dataType: mfilesProperty.TypedValue.DataType,
    displayValue: mfilesProperty.TypedValue.DisplayValue,
    property: mfilesProperty.PropertyDef,
    value: getValueFromMFilesProperty(mfilesProperty),
  };
};

export const getValueFromMFilesProperty = (mfilesProperty: IMFilesProperty): string => {
  if (false === mfilesProperty.TypedValue.HasValue) {
    return null;
  }
  if (mfilesProperty.TypedValue.DataType === PropertyDataType.Lookup) {
    return mfilesProperty.TypedValue.Lookup?.Item?.toString() ?? null;
  }
  if (mfilesProperty.TypedValue.DataType === PropertyDataType.MultiSelectLookup) {
    return mfilesProperty.TypedValue.Lookups?.map((l) => l.Item?.toString()).join(",") ?? null;
  }

  return mfilesProperty.TypedValue.Value ?? null;
};

export const toLookup = (id: string): ILookup => {
  return {
    Item: parseInt(id),
  };
};

export const createDefaultProperties = (
  propertyDefinitions: Map<number, IMFilesPropertyDefinition>,
  defaultPropertyValues: IDefaultPropertyValue[]
): IMFilesProperty[] => {
  return defaultPropertyValues.map((d) => {
    const value = d.useDateTimeNow ? format(Date.now(), MFILES_DATE_VALUE_FORMAT) : d.defaultValue;
    return createMfilesProperty(d.propertyId, propertyDefinitions.get(d.propertyId).DataType, value);
  });
};

export const createDateProperties = (
  newValue: Date | null,
  datePropertyDefinition: IMFilesPropertyDefinition,
  timePropertyDefinition?: IMFilesPropertyDefinition
): IMFilesProperty[] => {
  const properties: IMFilesProperty[] = [];
  const formattedValue = newValue ? format(newValue, MFILES_DATE_VALUE_FORMAT) : null;
  if (datePropertyDefinition) {
    properties.push(createMfilesProperty(datePropertyDefinition.ID, datePropertyDefinition.DataType, formattedValue));
  }
  if (timePropertyDefinition) {
    properties.push(createMfilesProperty(timePropertyDefinition.ID, timePropertyDefinition.DataType, formattedValue));
  }
  return properties;
};
